import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Form,
    Input,
    Modal,
    Popconfirm,
    Space,
    Table,
    Typography,
} from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { PosItemTypeRow } from "../../../model/pointofsale/PosItemTypeRow";
import { CreatePosItemTypeRequest } from "../../../requests/pointofsale/CreatePosItemTypeRequest";
import { UpdatePosItemTypeRequest } from "../../../requests/pointofsale/UpdatePosItemTypeRequest";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";

const { Text } = Typography;

// Settings panel for the per-client POS item-type CRUD (FORT-229). Mirrors
// FacilityTypesPanel: ignores the controlled value/onChange props because this
// section owns a list of rows, not a single value — it loads/persists each row
// directly through the settings service rather than bubbling state up to the
// shell.
export const PosItemTypesPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;

    const [rows, setRows] = useState<PosItemTypeRow[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const [editingId, setEditingId] = useState<number | 'new' | null>(null);
    const [draftName, setDraftName] = useState<string>('');
    const [saving, setSaving] = useState(false);

    const refresh = useCallback(async () => {
        if (!Number.isFinite(clientId)) return;
        try {
            const next = await service.listPosItemTypes(clientId);
            setRows(next);
            setLoadError(null);
        } catch (err) {
            setLoadError(err instanceof Error ? err.message : 'Failed to load POS item types.');
        }
    }, [service, clientId]);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError('Invalid client id in URL.');
            return;
        }
        setLoadError(null);
        void refresh();
    }, [clientId, refresh]);

    const handleOpenCreate = () => {
        setDraftName('');
        setEditingId('new');
        setActionError(null);
    };

    const handleOpenEdit = (row: PosItemTypeRow) => {
        setDraftName(row.name);
        setEditingId(row.id);
        setActionError(null);
    };

    const handleCloseModal = () => {
        if (saving) return;
        setEditingId(null);
        setActionError(null);
    };

    const handleSave = async () => {
        if (editingId === null) return;
        const trimmed = draftName.trim();
        if (trimmed === '') {
            setActionError('Name is required.');
            return;
        }
        setSaving(true);
        setActionError(null);
        try {
            if (editingId === 'new') {
                const payload: CreatePosItemTypeRequest = { name: trimmed };
                await service.createPosItemType(clientId, payload);
            } else {
                const payload: UpdatePosItemTypeRequest = { name: trimmed };
                await service.updatePosItemType(clientId, editingId, payload);
            }
            setEditingId(null);
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to save POS item type.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = useCallback(async (row: PosItemTypeRow) => {
        try {
            await service.deletePosItemType(clientId, row.id);
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to delete POS item type.');
        }
    }, [service, clientId, refresh]);

    const columns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'name' as const,
            render: (name: string, row: PosItemTypeRow) => (
                <span data-testid={`pit-row-name-${row.id}`}>{name}</span>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, row: PosItemTypeRow) => (
                <Space>
                    <Button
                        size="small"
                        data-testid={`pit-edit-${row.id}`}
                        onClick={() => handleOpenEdit(row)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this POS item type?"
                        description="Items already using this type will block deletion."
                        okText="Delete"
                        cancelText="Cancel"
                        onConfirm={() => handleDelete(row)}
                    >
                        <Button size="small" danger data-testid={`pit-delete-${row.id}`}>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ], [handleDelete]);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="pit-load-error" />;
    }
    if (rows === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                <Button
                    type="primary"
                    data-testid="pit-add-btn"
                    onClick={handleOpenCreate}
                >
                    Add POS item type
                </Button>
            </Space>
            {actionError && (
                <Alert type="error" message={actionError} data-testid="pit-action-error" />
            )}
            <Table
                rowKey="id"
                dataSource={rows}
                columns={columns}
                pagination={false}
                data-testid="pit-table"
                locale={{ emptyText: 'No POS item types yet.' }}
            />
            <Modal
                open={editingId !== null}
                title={editingId === 'new' ? 'Add POS item type' : 'Edit POS item type'}
                onCancel={handleCloseModal}
                onOk={handleSave}
                okText="Save"
                confirmLoading={saving}
                okButtonProps={{
                    disabled: draftName.trim() === '',
                    'data-testid': 'pit-modal-save',
                } as any}
                cancelButtonProps={{ disabled: saving }}
                destroyOnClose
            >
                <Form layout="vertical" data-testid="pit-form">
                    <Form.Item
                        label="Name"
                        validateStatus={draftName.trim() === '' ? 'error' : undefined}
                        help={draftName.trim() === '' ? 'Name is required.' : undefined}
                    >
                        <Input
                            data-testid="pit-form-name"
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            autoFocus
                            maxLength={100}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};
